// signals-web.ts — the WEB runtime's derived-signal computation.
//
// 🔴 WHY THIS EXISTS: `compute_signals` was a zero stub in web-commands.ts. On the web surface every
// figure came back 0 and every list empty, so the WearRack rendered nothing, the insights panel was
// blank, and the billed Recap/Ask calls were sent an EMPTY signals object — the model was being paid
// to describe a wardrobe it could not see. Native is unaffected (Rust computes it).
//
// Every rule below mirrors `src-tauri/src/signals.rs`. Where the two could drift, the Rust wins —
// it is the shipping surface. If you change a threshold here, change it there.
//
// The money figures are the reason this must be a faithful port rather than an approximation: the
// headline facts are handed to the model ALREADY CONTAINING the numbers precisely so it never has
// to compute (or invent) one. A web-only rounding difference would put a different figure in front
// of the same user on a different device.

import type { ClosetSignals, Garment } from "./store";

/** A wear row as stored: which garment, and the local calendar date it was worn. */
export interface WebWear {
  garment_id: string;
  worn_on: string;
}

const DAY = 86400000;
const parse = (s: string): number | null => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s ?? "")) return null;
  const t = Date.parse(`${s}T00:00:00Z`);
  return Number.isNaN(t) ? null : t;
};
const iso = (t: number) => new Date(t).toISOString().slice(0, 10);
const daysBetween = (from: number, to: number) => Math.max(0, Math.round((to - from) / DAY));
const round3 = (x: number) => Math.round(x * 1000) / 1000;
const pct = (r: number) => Math.round(r * 100);

/** Mirror of signals.rs::season_of — meteorological season, northern hemisphere. */
function seasonOf(t: number): string {
  const m = new Date(t).getUTCMonth() + 1;
  if (m === 12 || m === 1 || m === 2) return "winter";
  if (m >= 3 && m <= 5) return "spring";
  if (m >= 6 && m <= 8) return "summer";
  return "autumn";
}

/** Mirror of signals.rs::title. */
const title = (s: string) => (s ? s[0].toUpperCase() + s.slice(1) : "");

/** Mirror of the `money` closure in build_headline_facts — cents → "$1,234.50". */
function money(cents: number): string {
  const neg = cents < 0;
  const abs = Math.abs(cents);
  const whole = String(Math.floor(abs / 100)).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  const frac = String(abs % 100).padStart(2, "0");
  return `${neg ? "-" : ""}$${whole}.${frac}`;
}

const plural = (n: number) => (n === 1 ? "" : "s");

/** Mirror of signals.rs::compute. `todayStr` is the LOCAL calendar date (YYYY-MM-DD). */
export function computeSignalsWeb(garments: Garment[], wears: WebWear[], todayStr: string): ClosetSignals {
  const today = parse(todayStr) ?? Date.parse(`${new Date().toISOString().slice(0, 10)}T00:00:00Z`);

  // ── Index wear dates per garment (parsed once) ────────────────────────────
  const byGarment = new Map<string, number[]>();
  const seasonCounts = new Map<string, number>();
  let total_wears = 0;
  let lastWear: number | null = null;
  for (const w of wears) {
    const d = parse(w.worn_on);
    if (d === null) continue;
    byGarment.set(w.garment_id, [...(byGarment.get(w.garment_id) ?? []), d]);
    const s = seasonOf(d);
    seasonCounts.set(s, (seasonCounts.get(s) ?? 0) + 1);
    total_wears += 1;
    lastWear = lastWear === null ? d : Math.max(lastWear, d);
  }

  // ── Per-item summaries ────────────────────────────────────────────────────
  let total_spend_cents = 0;
  let items_worn = 0;
  let firstPurchase: number | null = null;

  let items = garments.map((g) => {
    const price_cents = Math.max(0, Number(g.price_cents ?? 0));
    total_spend_cents += price_cents;
    const purchase = parse(String(g.purchased_on ?? ""));
    if (purchase !== null) firstPurchase = firstPurchase === null ? purchase : Math.min(firstPurchase, purchase);

    const dates = byGarment.get(g.id) ?? [];
    const wears_n = dates.length;
    if (wears_n > 0) items_worn += 1;
    const last_worn = wears_n ? Math.max(...dates) : null;

    // Never worn => null, NOT 0. A 0 would read as "free", and it would sort to the top of the
    // best-value list — the opposite of the truth.
    const cost_per_wear_cents =
      wears_n > 0 ? (price_cents > 0 ? Math.round(price_cents / wears_n) : 0) : null;

    return {
      id: g.id,
      item: g.item,
      brand: g.brand ?? "",
      category: g.category && g.category.length ? g.category : "uncategorised",
      color: g.color ?? "",
      price_cents,
      purchased_on: String(g.purchased_on ?? ""),
      wears: wears_n,
      cost_per_wear_cents,
      // Anchored to the last wear if worn, else to today — mirrors the Rust exactly.
      days_since_purchase: purchase === null ? null : daysBetween(purchase, last_worn ?? today),
      last_worn_on: last_worn === null ? "" : iso(last_worn),
      days_dormant: last_worn === null ? null : daysBetween(last_worn, today),
    };
  });

  const items_never_worn = garments.length - items_worn;

  // Best (lowest) cost-per-wear first; never-worn last.
  items.sort((a, b) => {
    const x = a.cost_per_wear_cents, y = b.cost_per_wear_cents;
    if (x !== null && y !== null) return x - y;
    if (x !== null) return -1;
    if (y !== null) return 1;
    return 0;
  });

  // ── Dormancy lens: never-worn first, then longest-dormant. Capped at 12. ──
  const dormant = items
    .filter((i) => i.wears === 0 || (i.days_dormant !== null && i.days_dormant >= 90))
    .map((i) => ({
      item: i.item, category: i.category, wears: i.wears,
      last_worn_on: i.last_worn_on, days_dormant: i.days_dormant,
    }))
    .sort((a, b) => {
      if (a.days_dormant === null && b.days_dormant === null) return 0;
      if (a.days_dormant === null) return -1;
      if (b.days_dormant === null) return 1;
      return b.days_dormant - a.days_dormant;
    })
    .slice(0, 12);

  // ── Category balance ──────────────────────────────────────────────────────
  const catAcc = new Map<string, { items: number; wears: number; spend: number }>();
  for (const i of items) {
    const e = catAcc.get(i.category) ?? { items: 0, wears: 0, spend: 0 };
    e.items += 1; e.wears += i.wears; e.spend += i.price_cents;
    catAcc.set(i.category, e);
  }
  const categories = [...catAcc.entries()]
    .map(([category, e]) => ({
      category, items: e.items, wears: e.wears, spend_cents: e.spend,
      share_of_wears: total_wears > 0 ? round3(e.wears / total_wears) : 0,
    }))
    .sort((a, b) => b.wears - a.wears || b.items - a.items);

  // ── Season usage ──────────────────────────────────────────────────────────
  const seasons = ["winter", "spring", "summer", "autumn"].map((season) => {
    const w = seasonCounts.get(season) ?? 0;
    return { season, wears: w, share_of_wears: total_wears > 0 ? round3(w / total_wears) : 0 };
  });

  const avg_cost_per_wear_cents =
    total_wears > 0 && total_spend_cents > 0 ? Math.round(total_spend_cents / total_wears) : null;

  // ── Value deployed + never-worn spend ─────────────────────────────────────
  let never_worn_spend_cents = 0, worn_spend_cents = 0;
  for (const i of items) {
    if (i.wears === 0) never_worn_spend_cents += i.price_cents;
    else worn_spend_cents += i.price_cents;
  }
  const value_deployed_ratio = total_spend_cents > 0 ? round3(worn_spend_cents / total_spend_cents) : null;

  // ── Recent-activity window: last 30 days vs the 30 before ─────────────────
  const winStart = today - 30 * DAY;
  const prevStart = today - 60 * DAY;
  let wears_last_30d = 0, wears_prev_30d = 0;
  const recentItems = new Set<string>();
  for (const [gid, dates] of byGarment) {
    for (const d of dates) {
      if (d > winStart && d <= today) { wears_last_30d += 1; recentItems.add(gid); }
      else if (d > prevStart && d <= winStart) wears_prev_30d += 1;
    }
  }
  const delta_30d = wears_last_30d - wears_prev_30d;
  const direction = delta_30d > 0 ? "up" : delta_30d < 0 ? "down" : "flat";

  const mostWorn = items.filter((i) => i.wears > 0).reduce<typeof items[number] | null>(
    (best, i) => (best === null || i.wears > best.wears ? i : best), null);

  const activity = {
    wears_last_30d, wears_prev_30d, items_worn_last_30d: recentItems.size,
    delta_30d, direction,
    most_worn_item: mostWorn?.item ?? null,
    most_worn_count: mostWorn?.wears ?? 0,
  };

  // Days tracked: earliest of (first purchase, first wear) → today.
  const firstWear = wears.map((w) => parse(w.worn_on)).filter((d): d is number => d !== null);
  const earliest =
    firstPurchase !== null && firstWear.length ? Math.min(firstPurchase, ...firstWear)
      : firstPurchase !== null ? firstPurchase
      : firstWear.length ? Math.min(...firstWear)
      : null;

  const overall = {
    total_items: garments.length,
    total_wears,
    total_spend_cents,
    items_worn,
    items_never_worn,
    avg_cost_per_wear_cents,
    never_worn_spend_cents,
    value_deployed_ratio,
    first_purchase: firstPurchase === null ? null : iso(firstPurchase),
    last_wear: lastWear === null ? null : iso(lastWear),
    days_tracked: earliest === null ? 0 : daysBetween(earliest, today),
    currency_note: "amounts are in the user's local currency minor units (cents); the app formats them",
  };

  // Facts are built BEFORE the per-item list is truncated, so they can draw on every item.
  const headline_facts = buildHeadlineFacts(overall, items, dormant, categories, seasons, activity);
  items = items.slice(0, 40);

  return {
    schema:
      "oriora.closet.v2-signals: derived inventory aggregates only (no raw per-wear rows beyond per-item counts/dates). price_cents and *_cents fields are money in minor units (cents). cost_per_wear = price / wears. days_dormant = days since last wear. share_of_wears in 0..1. value_deployed_ratio = share of total spend that has >=1 wear. activity = wears in the last 30 days vs the 30 before. headline_facts = pre-ranked, ready-to-say observational sentences ALREADY containing the figures (lead with these; never invent a number). Every figure is a plain inventory observation — describe the user's own numbers only; NEVER give style, buying, keeping, donating, decluttering, resale, or body/health advice; say 'worn N times', never 'wear it more'.",
    generated_for: iso(today),
    overall, items, dormant, categories, seasons, activity, headline_facts,
  } as unknown as ClosetSignals;
}

/** Mirror of signals.rs::build_headline_facts. Order matters — the recap leads with fact #1. */
function buildHeadlineFacts(
  overall: ReturnType<typeof computeSignalsWeb> extends never ? never : any,
  items: { id: string; item: string; price_cents: number; wears: number; cost_per_wear_cents: number | null }[],
  dormant: { item: string; wears: number; days_dormant: number | null }[],
  categories: { category: string; items: number; wears: number; share_of_wears: number }[],
  seasons: { season: string; wears: number; share_of_wears: number }[],
  activity: { wears_last_30d: number; wears_prev_30d: number; items_worn_last_30d: number; direction: string; most_worn_item: string | null; most_worn_count: number },
): { kind: string; text: string; tone: string }[] {
  const facts: { kind: string; text: string; tone: string }[] = [];

  // 1) Best cost-per-wear (items are already sorted best-first).
  const best = items.find((i) => i.cost_per_wear_cents !== null && i.wears > 0);
  if (best && best.cost_per_wear_cents !== null) {
    facts.push({
      kind: "cost_per_wear",
      text: `Your best value is ${best.item} at ${money(best.cost_per_wear_cents)} a wear — ${money(best.price_cents)} over ${best.wears} wear${plural(best.wears)}.`,
      tone: "good",
    });
  }

  // 1b) Costliest-per-wear worn item — only once there is a real spread.
  const priced = items.filter((i) => i.cost_per_wear_cents !== null && i.wears > 0 && i.price_cents > 0);
  const worst = priced.reduce<typeof items[number] | null>(
    (w, i) => (w === null || (i.cost_per_wear_cents ?? 0) > (w.cost_per_wear_cents ?? 0) ? i : w), null);
  if (worst && worst.cost_per_wear_cents !== null && best && worst.id !== best.id
      && priced.length >= 2 && worst.cost_per_wear_cents >= 100) {
    facts.push({
      kind: "cost_per_wear",
      text: `Your highest cost-per-wear is ${worst.item} at ${money(worst.cost_per_wear_cents)} a wear (${money(worst.price_cents)} over ${worst.wears} wear${plural(worst.wears)}).`,
      tone: "watch",
    });
  }

  // 2) Recent activity.
  if (activity.wears_last_30d > 0) {
    if (activity.direction !== "flat" && activity.wears_prev_30d > 0) {
      facts.push({
        kind: "activity",
        text: `You've logged ${activity.wears_last_30d} wear${plural(activity.wears_last_30d)} across ${activity.items_worn_last_30d} item${plural(activity.items_worn_last_30d)} in the last 30 days — ${activity.direction} from ${activity.wears_prev_30d} the month before.`,
        tone: activity.direction === "up" ? "good" : "neutral",
      });
    } else {
      facts.push({
        kind: "activity",
        text: `You've logged ${activity.wears_last_30d} wear${plural(activity.wears_last_30d)} across ${activity.items_worn_last_30d} item${plural(activity.items_worn_last_30d)} in the last 30 days.`,
        tone: "neutral",
      });
    }
  }

  // 3) Never-worn count + the spend sitting idle.
  if (overall.items_never_worn > 0) {
    const spendClause = overall.never_worn_spend_cents > 0
      ? `, ${money(overall.never_worn_spend_cents)} of purchases` : "";
    facts.push({
      kind: "dormant",
      text: `${overall.items_never_worn} of your ${overall.total_items} item${plural(overall.total_items)} ${overall.items_never_worn === 1 ? "has" : "have"} no logged wears yet${spendClause}.`,
      tone: "watch",
    });
  }

  // 3b) Value-deployed ratio.
  if (overall.value_deployed_ratio !== null && overall.total_spend_cents > 0) {
    const p = pct(overall.value_deployed_ratio);
    facts.push({
      kind: "value",
      text: `${p}% of your wardrobe spend (${money(overall.total_spend_cents - overall.never_worn_spend_cents)} of ${money(overall.total_spend_cents)}) has earned at least one wear.`,
      tone: p >= 70 ? "good" : "neutral",
    });
  }

  // 4) Longest-dormant WORN item — distinct from never-worn: a piece she used, then stopped.
  const stopped = dormant.find((d) => d.wears > 0 && d.days_dormant !== null && d.days_dormant >= 90);
  if (stopped && stopped.days_dormant !== null) {
    const days = stopped.days_dormant;
    const span = days >= 365 ? "over a year" : `${Math.round(days / 30)} months`;
    facts.push({
      kind: "dormant",
      text: `Your ${stopped.item} hasn't been worn in ${span} (${stopped.wears} wear${plural(stopped.wears)} logged).`,
      tone: "watch",
    });
  }

  // 5) Top category by wears.
  const topCat = categories.find((c) => c.wears > 0);
  if (topCat) {
    const name = title(topCat.category);
    facts.push({
      kind: "category",
      text: `${name} lead${name.endsWith("s") ? "" : "s"} your wear log at ${pct(topCat.share_of_wears)}% of wears (${topCat.wears} wear${plural(topCat.wears)} across ${topCat.items} item${plural(topCat.items)}).`,
      tone: "neutral",
    });
  }

  // 6) Peak season.
  const peak = seasons.filter((s) => s.wears > 0).reduce<typeof seasons[number] | null>(
    (p, s) => (p === null || s.wears > p.wears ? s : p), null);
  if (peak) {
    facts.push({
      kind: "season",
      text: `Your closet is busiest in ${peak.season} — ${peak.wears} wear${plural(peak.wears)} there (${pct(peak.share_of_wears)}% of all wears).`,
      tone: "neutral",
    });
  }

  // 7) Most-worn single piece.
  if (activity.most_worn_item && activity.most_worn_count >= 2) {
    facts.push({
      kind: "value",
      text: `Your most-worn piece is ${activity.most_worn_item} at ${activity.most_worn_count} wears.`,
      tone: "good",
    });
  }

  // 7b) Closet-wide cost-per-wear.
  if (overall.avg_cost_per_wear_cents !== null && overall.total_wears > 0) {
    facts.push({
      kind: "cost_per_wear",
      text: `Across everything you've worn, your wardrobe averages ${money(overall.avg_cost_per_wear_cents)} a wear (${money(overall.total_spend_cents)} over ${overall.total_wears} wear${plural(overall.total_wears)}).`,
      tone: "neutral",
    });
  }

  // 7c) Category breadth.
  const wornCats = categories.filter((c) => c.wears > 0);
  if (wornCats.length >= 2) {
    facts.push({
      kind: "category",
      text: `Your wears span ${wornCats.length} categories, led by ${title(wornCats[0].category)}.`,
      tone: "neutral",
    });
  }

  // 8) Wardrobe-scale milestone.
  if (overall.total_wears > 0 && overall.days_tracked >= 14) {
    facts.push({
      kind: "milestone",
      text: `Across ${overall.days_tracked} day${plural(overall.days_tracked)}, you've logged ${overall.total_wears} wear${plural(overall.total_wears)} over ${overall.total_items} item${plural(overall.total_items)}.`,
      tone: "neutral",
    });
  }

  return facts;
}
